import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import { AgentAvatar } from './AgentAvatars';
import { Users, CheckCircle, Clock, Zap, Plus, ArrowRight, Rocket } from 'lucide-react';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [agentsRes, activityRes] = await Promise.all([
        api.get('/api/agents/deployed'),
        api.get('/api/activity')
      ]);
      setAgents(agentsRes.data || []);
      setActivity((activityRes.data || []).slice(0, 8));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const totalTasks = agents.reduce((sum, a) => sum + (a.metrics?.tasks_completed || 0), 0);
  const totalMessages = agents.reduce((sum, a) => sum + (a.metrics?.messages_sent || 0), 0);
  const totalHours = agents.reduce((sum, a) => sum + (a.metrics?.hours_saved || 0), 0);
  const activeCount = agents.filter(a => a.status === 'active').length;

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString();
  };

  if (loading) return <div className="loading-screen" style={{ height: 400, background: 'transparent' }}><div className="spinner" style={{ borderTopColor: 'var(--panzix-green)' }} /></div>;

  return (
    <div className="fade-in">
      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)' }}>
            <Users size={22} color="#16a34a" />
          </div>
          <div className="stat-value" data-testid="active-agents-count">{activeCount}</div>
          <div className="stat-label">Active AI Employees</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)' }}>
            <CheckCircle size={22} color="#4f46e5" />
          </div>
          <div className="stat-value" data-testid="total-tasks">{totalTasks}</div>
          <div className="stat-label">Tasks Completed</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #fef3c7, #fde68a)' }}>
            <Zap size={22} color="#d97706" />
          </div>
          <div className="stat-value" data-testid="total-messages">{totalMessages}</div>
          <div className="stat-label">Messages Sent</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #fce7f3, #fbcfe8)' }}>
            <Clock size={22} color="#db2777" />
          </div>
          <div className="stat-value" data-testid="hours-saved">{totalHours.toFixed(1)}</div>
          <div className="stat-label">Hours Saved</div>
        </div>
      </div>

      {/* Deployed Agents */}
      <div className="section-header">
        <h3>Your AI Employees</h3>
        <button className="btn-action" onClick={() => navigate('/deploy')} data-testid="deploy-btn">
          <Plus size={16} style={{ marginRight: 6 }} />Deploy New
        </button>
      </div>

      {agents.length === 0 ? (
        <div className="empty-state">
          <Users size={64} />
          <h4>No AI Employees Deployed Yet</h4>
          <p>Deploy your first AI employee to start automating HR tasks and saving time.</p>
          <button className="btn-action" onClick={() => navigate('/deploy')} data-testid="deploy-first-btn">
            <Rocket size={16} style={{ marginRight: 6 }} />Deploy Your First AI Employee
          </button>
        </div>
      ) : (
        <div className="agents-grid">
          {agents.map(agent => (
            <div key={agent.id} className="agent-card" onClick={() => navigate(`/workspace/${agent.id}`)} data-testid={`agent-card-${agent.agent_type}`}
              style={{ '--card-color': agent.color }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: agent.color }} />
              <div className="agent-card-header">
                <div className="agent-avatar">
                  <AgentAvatar agentType={agent.agent_type} size={56} />
                </div>
                <div className="agent-info">
                  <h4>{agent.agent_name}</h4>
                  <p style={{ color: agent.color }}>{agent.agent_title}</p>
                </div>
              </div>
              <p className="description">{agent.description}</p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className={`agent-status ${agent.status}`}>
                  <span className={`status-dot ${agent.status}`} />
                  {agent.status.charAt(0).toUpperCase() + agent.status.slice(1)}
                </span>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{formatTime(agent.last_activity)}</span>
              </div>
              <div className="agent-specialties">
                {(agent.specialties || []).slice(0, 3).map(s => (
                  <span key={s} className="specialty-tag">{s}</span>
                ))}
              </div>
              <div className="agent-metrics">
                <div className="metric">
                  <div className="value">{agent.metrics?.tasks_completed || 0}</div>
                  <div className="label">Tasks</div>
                </div>
                <div className="metric">
                  <div className="value">{agent.metrics?.messages_sent || 0}</div>
                  <div className="label">Messages</div>
                </div>
                <div className="metric">
                  <div className="value">{(agent.metrics?.hours_saved || 0).toFixed(1)}</div>
                  <div className="label">Hrs Saved</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent Activity */}
      {activity.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <div className="section-header">
            <h3>Recent Activity</h3>
          </div>
          <div style={{ background: 'white', borderRadius: 14, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
            {activity.map((evt, i) => (
              <div key={evt.id || i} className="activity-item">
                <div className="activity-dot" style={{ background: evt.event_type?.includes('completed') ? '#22c55e' : evt.event_type?.includes('failed') ? '#ef4444' : '#3b82f6' }} />
                <div>
                  <div className="activity-text">
                    <strong>{evt.agent_name || 'System'}</strong> — {evt.message}
                  </div>
                  <div className="activity-time">{formatTime(evt.timestamp)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
